import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  BookOpen, 
  Users, 
  DollarSign, 
  Star, 
  TrendingUp, 
  Video,
  ArrowUpRight,
  Sparkles,
  ChevronRight,
  Clock,
  Eye,
  Radio,
  Calendar,
  Play,
  ExternalLink,
  Wallet,
  ShieldAlert,
  ArrowRight,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store/auth';
import { usersApi, coursesApi, meetingsApi, paymentsApi } from '../../lib/api';
import { formatPrice } from '../../lib/utils';
import { useAnalytics, AnalyticsEvents } from '../../hooks/useAnalytics';

interface Stats {
  totalCourses: number;
  publishedCourses: number;
  totalStudents: number;
  totalEarnings: number;
  totalReviews: number;
  averageRating: number;
}

interface Course {
  id: string;
  title: string;
  thumbnailUrl?: string;
  status: string;
  totalStudents: number;
  rating?: number;
  price: number;
}

interface Meeting {
  id: string;
  title: string;
  scheduledAt: string;
  duration: number;
  status: 'SCHEDULED' | 'LIVE' | 'ENDED' | 'CANCELLED';
  jitsiRoomId: string;
  course: {
    id: string;
    title: string;
  };
}

import { joinMeetingSecure } from '../../lib/jitsi';

interface ConnectStatus {
  hasAccount: boolean;
  isOnboarded: boolean;
  payoutsEnabled?: boolean;
}

export default function ProfessorDashboard() {
  const { user } = useAuthStore();
  const { trackEvent, trackMeetingJoined } = useAnalytics();
  const [stats, setStats] = useState<Stats | null>(null);
  const [recentCourses, setRecentCourses] = useState<Course[]>([]);
  const [upcomingMeetings, setUpcomingMeetings] = useState<Meeting[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [connectStatus, setConnectStatus] = useState<ConnectStatus | null>(null);
  const [isOpeningDashboard, setIsOpeningDashboard] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, coursesRes, meetingsRes, connectRes] = await Promise.all([
          usersApi.getStats(),
          coursesApi.getMyCourses(),
          meetingsApi.getUpcoming({ limit: 3 }),
          paymentsApi.getConnectStatus().catch(() => null)
        ]);
        
        if (statsRes.data.success) {
          setStats(statsRes.data.data as Stats);
        }
        if (coursesRes.data.success) {
          setRecentCourses((coursesRes.data.data as Course[]).slice(0, 3));
        }
        if (meetingsRes.data.success) {
          setUpcomingMeetings(meetingsRes.data.data as Meeting[]);
        }
        if (connectRes?.data.success && connectRes.data.data) {
          setConnectStatus(connectRes.data.data);
        }
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleEarningsClick = async () => {
    // If no account or not onboarded, redirect to onboarding
    if (!connectStatus?.hasAccount || !connectStatus?.isOnboarded) {
      try {
        setIsOpeningDashboard(true);
        const response = await paymentsApi.connectOnboard({
          returnUrl: `${window.location.origin}/professor`,
          refreshUrl: `${window.location.origin}/professor?refresh=true`,
        });
        
        if (response.data.success && response.data.data?.onboardingUrl) {
          window.location.href = response.data.data.onboardingUrl;
        } else {
          toast.error('Failed to start payment setup');
        }
      } catch (error) {
        console.error('Failed to start onboarding:', error);
        toast.error('Failed to start payment setup');
      } finally {
        setIsOpeningDashboard(false);
      }
      return;
    }

    // Open Stripe dashboard for managing payouts
    try {
      setIsOpeningDashboard(true);
      const response = await paymentsApi.getDashboardLink();
      
      if (response.data.success && response.data.data?.url) {
        window.open(response.data.data.url, '_blank');
      } else {
        toast.error('Failed to open payout dashboard');
      }
    } catch (error) {
      console.error('Failed to get dashboard link:', error);
      toast.error('Failed to open payout dashboard');
    } finally {
      setIsOpeningDashboard(false);
    }
  };

  const handleStartMeeting = async (meeting: Meeting) => {
    try {
      await meetingsApi.start(meeting.id);
      // Track meeting started by professor
      trackEvent(AnalyticsEvents.MEETING_STARTED, {
        meetingId: meeting.id,
        courseId: meeting.course.id,
      });
      try {
        trackMeetingJoined(meeting.id, meeting.course.id, true);
        const result = await joinMeetingSecure(meeting.id);
        console.log('Join meeting result:', result);
      } catch (error) {
        console.error('JWT join failed:', error);
      }
    } catch {
      console.error('Failed to start meeting');
    }
  };

  const handleJoinMeeting = async (meeting: Meeting) => {
    try {
      // Track meeting joined by professor (as host)
      trackMeetingJoined(meeting.id, meeting.course.id, true);
      const result = await joinMeetingSecure(meeting.id);
      console.log('Join meeting result:', result);
    } catch (error) {
      console.error('JWT join failed:', error);
    }
  };

  const getTimeGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="skeleton h-32 rounded-3xl" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="skeleton h-40 rounded-2xl" />
          ))}
        </div>
        <div className="skeleton h-64 rounded-2xl" />
      </div>
    );
  }

  // Determine earnings card action text based on connect status
  const getEarningsActionText = () => {
    if (!connectStatus?.hasAccount) return 'Set up payouts';
    if (!connectStatus?.isOnboarded) return 'Complete setup';
    return 'Manage payouts';
  };

  const statCards = [
    { 
      label: 'Total Courses', 
      value: stats?.totalCourses || 0, 
      icon: BookOpen, 
      gradient: 'from-blue-500/20 to-cyan-500/20',
      iconColor: 'text-blue-400',
      trend: `${stats?.publishedCourses || 0} published`,
      onClick: undefined,
      actionText: undefined,
    },
    { 
      label: 'Total Students', 
      value: stats?.totalStudents || 0, 
      icon: Users, 
      gradient: 'from-emerald-500/20 to-green-500/20',
      iconColor: 'text-emerald-400',
      trend: 'Enrolled across all courses',
      onClick: undefined,
      actionText: undefined,
    },
    { 
      label: 'Total Earnings', 
      value: formatPrice(stats?.totalEarnings || 0), 
      icon: DollarSign, 
      gradient: 'from-amber-500/20 to-yellow-500/20',
      iconColor: 'text-amber-400',
      trend: 'Lifetime earnings',
      onClick: handleEarningsClick,
      actionText: getEarningsActionText(),
      isLoading: isOpeningDashboard,
    },
    { 
      label: 'Average Rating', 
      value: stats?.averageRating?.toFixed(1) || '0.0', 
      icon: Star, 
      gradient: 'from-purple-500/20 to-pink-500/20',
      iconColor: 'text-purple-400',
      trend: `${stats?.totalReviews || 0} reviews`,
      onClick: undefined,
      actionText: undefined,
    },
  ];

  const quickActions = [
    { 
      title: 'Manage Courses', 
      description: 'View, edit and organize your course library', 
      href: '/professor/courses', 
      icon: BookOpen,
      gradient: 'from-slate-500/10 to-slate-600/10'
    },
    { 
      title: 'View Analytics', 
      description: 'Track performance and student engagement', 
      href: '/professor/analytics', 
      icon: TrendingUp,
      gradient: 'from-blue-500/10 to-cyan-500/10'
    },
    { 
      title: 'Live Sessions', 
      description: 'Schedule and manage live meetings', 
      href: '/professor/meetings', 
      icon: Video,
      gradient: 'from-coral/10 to-red-500/10'
    },
  ];

  // Check verification status
  const isVerified = user?.verificationStatus === 'VERIFIED';
  const isPendingVerification = user?.verificationStatus === 'PENDING';
  const needsVerification = !isVerified && !isPendingVerification;

  return (
    <div className="space-y-6 sm:space-y-8 max-w-7xl overflow-x-hidden">
      {/* Verification Warning Banner - Show for unverified professors */}
      {needsVerification && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-2xl p-4 sm:p-5"
          style={{
            background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 70, 74, 0.1) 100%)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
          }}
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-center gap-4 w-full sm:w-auto">
              <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-red-500/20 flex items-center justify-center">
                <ShieldAlert className="w-5 h-5 sm:w-6 sm:h-6 text-red-400" />
              </div>
              <div className="flex-1 min-w-0 sm:hidden">
                <h3 className="text-sm font-semibold text-white">Identity Verification Required</h3>
                <p className="text-xs text-white/60 mt-0.5">
                  Verify your identity to submit courses and start earning.
                </p>
              </div>
            </div>
            <div className="hidden sm:block flex-1 min-w-0">
              <h3 className="text-base font-semibold text-white">Identity Verification Required</h3>
              <p className="text-sm text-white/60 mt-0.5">
                Verify your identity to submit courses for review and start earning. It only takes a few minutes.
              </p>
            </div>
            <Link
              to="/professor/verification"
              className="w-full sm:w-auto flex-shrink-0 flex items-center justify-center gap-2 px-4 sm:px-5 py-2.5 rounded-xl font-medium text-sm text-white transition-all hover:scale-105"
              style={{
                background: 'linear-gradient(135deg, #dc464a 0%, #c63b3f 100%)',
                boxShadow: '0 4px 15px rgba(220, 70, 74, 0.3)',
              }}
            >
              Verify Now
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </motion.div>
      )}

      {/* Pending Verification Banner */}
      {isPendingVerification && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-2xl p-4 sm:p-5"
          style={{
            background: 'linear-gradient(135deg, rgba(234, 179, 8, 0.15) 0%, rgba(245, 158, 11, 0.1) 100%)',
            border: '1px solid rgba(234, 179, 8, 0.3)',
          }}
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-center gap-4 w-full sm:w-auto">
              <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center">
                <ShieldAlert className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-400 animate-pulse" />
              </div>
              <div className="flex-1 min-w-0 sm:hidden">
                <h3 className="text-sm font-semibold text-white">Verification In Progress</h3>
                <p className="text-xs text-white/60 mt-0.5">
                  Your verification is being processed.
                </p>
              </div>
            </div>
            <div className="hidden sm:block flex-1 min-w-0">
              <h3 className="text-base font-semibold text-white">Verification In Progress</h3>
              <p className="text-sm text-white/60 mt-0.5">
                Your identity verification is being processed. This usually takes a few minutes.
              </p>
            </div>
            <Link
              to="/professor/verification"
              className="w-full sm:w-auto flex-shrink-0 flex items-center justify-center gap-2 px-4 sm:px-5 py-2.5 rounded-xl font-medium text-sm text-yellow-300 bg-yellow-500/20 hover:bg-yellow-500/30 transition-colors"
            >
              Check Status
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </motion.div>
      )}

      {/* Premium Welcome Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-2xl sm:rounded-3xl p-5 sm:p-8 lg:p-10"
        style={{
          background: 'linear-gradient(135deg, rgba(42, 74, 91, 0.15) 0%, rgba(15, 16, 20, 0.95) 50%, rgba(220, 70, 74, 0.08) 100%)',
          border: '1px solid rgba(255, 255, 255, 0.06)'
        }}
      >
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-coral/10 via-transparent to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-slate/20 via-transparent to-transparent rounded-full blur-2xl" />
        
        <div className="relative z-10 flex items-start justify-between">
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
                <Sparkles className="w-4 h-4 text-amber-400" />
                <span className="text-sm font-medium text-white/70">Professor Dashboard</span>
              </div>
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
              {getTimeGreeting()}, {user?.firstName}
            </h1>
            <p className="text-sm sm:text-lg text-white/50 max-w-xl">
              Here's an overview of your teaching performance. Keep inspiring your students!
            </p>
          </div>
          
          {/* Avatar */}
          <div className="hidden lg:block">
            {user?.avatarUrl ? (
              <img 
                src={user.avatarUrl} 
                alt={user.firstName}
                className="w-20 h-20 rounded-2xl object-cover ring-4 ring-white/10"
              />
            ) : (
              <div 
                className="w-20 h-20 rounded-2xl flex items-center justify-center text-2xl font-bold text-white ring-4 ring-white/10"
                style={{ background: 'linear-gradient(135deg, #dc464a 0%, #f97171 100%)' }}
              >
                {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
        {statCards.map((stat, index) => {
          const isClickable = !!stat.onClick;
          const CardWrapper = isClickable ? 'button' : 'div';
          
          return (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <CardWrapper
                onClick={stat.onClick}
                {...(isClickable ? { disabled: stat.isLoading } : {})}
                className={`group relative overflow-hidden rounded-2xl p-6 transition-all duration-300 hover:scale-[1.02] w-full text-left ${
                  isClickable ? 'cursor-pointer' : ''
                } ${stat.isLoading ? 'opacity-70 pointer-events-none' : ''}`}
                style={{
                  background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.03) 0%, rgba(15, 16, 20, 0.95) 100%)',
                  border: '1px solid rgba(255, 255, 255, 0.06)'
                }}
              >
                {/* Gradient overlay on hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                
                <div className="relative z-10">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${stat.iconColor} bg-white/5`}>
                    <stat.icon className="w-6 h-6" />
                  </div>
                  <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
                  <div className="text-sm font-medium text-white/70 mb-2">{stat.label}</div>
                  <div className="text-xs text-white/40">{stat.trend}</div>
                  
                  {/* Action button for clickable cards */}
                  {isClickable && stat.actionText && (
                    <div className="mt-3 pt-3 border-t border-white/10">
                      <div className="flex items-center gap-1.5 text-xs font-medium text-amber-400 group-hover:text-amber-300 transition-colors">
                        {stat.isLoading ? (
                          <>
                            <div className="w-3 h-3 border-2 border-amber-400/30 border-t-amber-400 rounded-full animate-spin" />
                            <span>Opening...</span>
                          </>
                        ) : (
                          <>
                            <Wallet className="w-3.5 h-3.5" />
                            <span>{stat.actionText}</span>
                            <ExternalLink className="w-3 h-3 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </CardWrapper>
            </motion.div>
          );
        })}
      </div>

      {/* Two Column Layout */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="lg:col-span-2 rounded-2xl p-6"
          style={{
            background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.02) 0%, rgba(15, 16, 20, 0.95) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.06)'
          }}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-white">Quick Actions</h2>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            {quickActions.map((action) => (
              <Link
                key={action.title}
                to={action.href}
                className="group relative overflow-hidden rounded-xl p-5 transition-all duration-300 hover:scale-[1.02]"
                style={{
                  background: 'rgba(255, 255, 255, 0.02)',
                  border: '1px solid rgba(255, 255, 255, 0.06)'
                }}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${action.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                
                <div className="relative z-10">
                  <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center mb-4 group-hover:bg-white/10 transition-colors">
                    <action.icon className="w-5 h-5 text-white/60 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="font-semibold text-white mb-1 group-hover:text-white transition-colors">
                    {action.title}
                  </h3>
                  <p className="text-sm text-white/40 line-clamp-2">{action.description}</p>
                  <div className="mt-4 flex items-center text-sm font-medium text-coral opacity-0 group-hover:opacity-100 transition-opacity">
                    <span>Open</span>
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </motion.div>

        {/* Recent Courses */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="rounded-2xl p-6"
          style={{
            background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.02) 0%, rgba(15, 16, 20, 0.95) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.06)'
          }}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-white">Your Courses</h2>
            <Link 
              to="/professor/courses" 
              className="text-sm font-medium text-coral hover:text-coral/80 transition-colors flex items-center gap-1"
            >
              View all
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          
          {recentCourses.length > 0 ? (
            <div className="space-y-4">
              {recentCourses.map((course) => (
                <Link
                  key={course.id}
                  to={`/professor/courses/${course.id}/edit`}
                  className="group flex items-center gap-4 p-3 rounded-xl hover:bg-white/5 transition-colors"
                >
                  <div className="w-14 h-14 rounded-lg overflow-hidden bg-white/5 flex-shrink-0">
                    {course.thumbnailUrl ? (
                      <img 
                        src={course.thumbnailUrl} 
                        alt={course.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <BookOpen className="w-6 h-6 text-white/30" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-white truncate group-hover:text-coral transition-colors">
                      {course.title}
                    </h3>
                    <div className="flex items-center gap-3 mt-1 text-xs text-white/40">
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {course.totalStudents}
                      </span>
                      {course.rating && (
                        <span className="flex items-center gap-1">
                          <Star className="w-3 h-3 text-amber-400" />
                          {course.rating.toFixed(1)}
                        </span>
                      )}
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        course.status === 'PUBLISHED' 
                          ? 'bg-emerald-500/20 text-emerald-400' 
                          : 'bg-amber-500/20 text-amber-400'
                      }`}>
                        {course.status.toLowerCase()}
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-white/20 group-hover:text-white/40 transition-colors" />
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-8 h-8 text-white/30" />
              </div>
              <p className="text-white/50 mb-4">No courses yet</p>
              <Link 
                to="/professor/courses/new"
                className="inline-flex items-center gap-2 text-sm font-medium text-coral hover:text-coral/80 transition-colors"
              >
                Create your first course
                <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </motion.div>
      </div>

      {/* Upcoming Live Sessions */}
      {upcomingMeetings.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="rounded-2xl p-6"
          style={{
            background: 'linear-gradient(145deg, rgba(139, 92, 246, 0.08) 0%, rgba(15, 16, 20, 0.95) 100%)',
            border: '1px solid rgba(139, 92, 246, 0.15)'
          }}
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                <Radio className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Upcoming Live Sessions</h2>
                <p className="text-sm text-white/50">{upcomingMeetings.length} session{upcomingMeetings.length !== 1 ? 's' : ''} scheduled</p>
              </div>
            </div>
            <Link 
              to="/professor/meetings" 
              className="text-sm font-medium text-purple-400 hover:text-purple-300 transition-colors flex items-center gap-1"
            >
              Manage all
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {upcomingMeetings.map((meeting) => {
              const isLive = meeting.status === 'LIVE';
              const meetingDate = new Date(meeting.scheduledAt);
              const isToday = new Date().toDateString() === meetingDate.toDateString();

              return (
                <div
                  key={meeting.id}
                  className={`p-4 rounded-xl transition-all ${
                    isLive 
                      ? 'bg-gradient-to-br from-red-500/20 to-purple-500/10 border-2 border-red-500/40' 
                      : 'bg-white/[0.03] border border-white/10 hover:border-purple-500/30'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-white truncate mb-1">{meeting.title}</h3>
                      <p className="text-xs text-white/40 truncate">{meeting.course.title}</p>
                    </div>
                    {isLive && (
                      <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400 animate-pulse">
                        <Radio className="w-3 h-3" />
                        LIVE
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-3 text-sm text-white/50 mb-4">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      {isToday ? 'Today' : meetingDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {meetingDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>

                  {isLive ? (
                    <button
                      onClick={() => handleJoinMeeting(meeting)}
                      className="w-full py-2 px-4 rounded-lg bg-red-500 text-white font-medium flex items-center justify-center gap-2 hover:bg-red-600 transition-colors"
                    >
                      <Radio className="w-4 h-4" />
                      Join Live Session
                    </button>
                  ) : (
                    <button
                      onClick={() => handleStartMeeting(meeting)}
                      className="w-full py-2 px-4 rounded-lg bg-purple-500/20 text-purple-300 font-medium flex items-center justify-center gap-2 hover:bg-purple-500/30 transition-colors"
                    >
                      <Play className="w-4 h-4" />
                      Start Session
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* Performance Tips */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="rounded-2xl p-6 lg:p-8"
        style={{
          background: 'linear-gradient(135deg, rgba(220, 70, 74, 0.08) 0%, rgba(15, 16, 20, 0.95) 50%, rgba(42, 74, 91, 0.1) 100%)',
          border: '1px solid rgba(255, 255, 255, 0.06)'
        }}
      >
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-coral/20 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-6 h-6 text-coral" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white mb-2">Tips to Boost Your Performance</h3>
            <p className="text-white/50 mb-4">Here are some recommendations to help you grow your teaching business.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5">
                <Clock className="w-5 h-5 text-blue-400 flex-shrink-0" />
                <span className="text-sm text-white/70">Schedule regular live sessions</span>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5">
                <Eye className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                <span className="text-sm text-white/70">Add preview videos to courses</span>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5">
                <Star className="w-5 h-5 text-amber-400 flex-shrink-0" />
                <span className="text-sm text-white/70">Respond to student reviews</span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
